import { MsalProvider } from '@azure/msal-react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { loginRequest, msalInstance } from './auth/msal';
import { linkLoginHint } from './auth/linkHint';
import { claimAutoSignIn, linkAccount, onSignInEvent, signedInAccount, signedOutOnPurpose } from './auth/ssoMemory';
import './styles/facilitywise.css';
import './styles/facilitywise-app.css';
import './styles/facilitywise-clock.css';

/** The page carrying Entra's answer, or MSAL's hidden renewal frame: neither
 *  ever starts a sign-in of its own. */
const isAuthResponse = /[?#&](code|error)=/.test(window.location.hash + window.location.search);
const inHiddenFrame = window.self !== window.top;

function render(): void {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </MsalProvider>
    </StrictMode>,
  );
}

async function bootstrap(): Promise<void> {
  await msalInstance.initialize();

  // EVERY LINK LANDS SIGNED IN (the platform rule, Chauncey White 2026-10-23 —
  // auth/ssoMemory.ts). Remember who signed in — the hint for the next link
  // that carries none — and make them this tab's account. Whether they signed
  // out ON PURPOSE is the menu's to say (AccountMenu.tsx), never an MSAL event.
  // ⚠ msal-browser 3.x: LOGIN_SUCCESS carries an AuthenticationResult
  // (payload.account), unlike 5.x — onSignInEvent reads that shape.
  msalInstance.addEventCallback((event) => onSignInEvent(event, msalInstance));

  const justSignedIn = await msalInstance.handleRedirectPromise();

  // The cache is shared across tabs now: a tab opened from a link uses the
  // account already there — the one the link NAMES (auth/linkHint.ts). A link
  // for somebody the browser does not hold signs them in instead of opening as
  // whoever is cached (on a shared PC, the last person who did not sign out) —
  // once per tab, never after a chosen sign-out, never on the page carrying
  // Entra's answer or inside the renewal frame. A sign-in that has just come
  // back from Entra is who this tab is: the link does not send it round again.
  // Adopting the account before first render also keeps the sign-in screen
  // from flashing for someone who is already signed in.
  const accounts = msalInstance.getAllAccounts();
  const linked = justSignedIn
    ? { account: null, signInAs: null }
    : linkAccount(accounts, linkLoginHint(window.location.search, window.location.hash));
  if (linked.account !== null) msalInstance.setActiveAccount(linked.account);
  else if (!msalInstance.getActiveAccount() && accounts.length > 0) {
    msalInstance.setActiveAccount(signedInAccount(msalInstance, accounts) ?? null);
  }
  if (linked.signInAs !== null && !isAuthResponse && !inHiddenFrame && !signedOutOnPurpose() && claimAutoSignIn()) {
    msalInstance.loginRedirect({ ...loginRequest, loginHint: linked.signInAs }).catch((err: unknown) => {
      // The redirect itself failed (not Entra refusing): open as whoever is
      // cached rather than a blank page — the sign-in gate never runs here.
      console.warn("[facilitywise] sign-in for the link's person failed:", err);
      render();
    });
    return;
  }
  render();
}

void bootstrap();
